import React from 'react';
import { ClerkProvider } from '@clerk/clerk-react';

// Get Clerk publishable key and URL paths from environment variables (production-safe)
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const signInUrl = import.meta.env.VITE_CLERK_SIGN_IN_URL ?? '/sign-in';
const signUpUrl = import.meta.env.VITE_CLERK_SIGN_UP_URL ?? '/sign-up';
const afterSignInUrl = import.meta.env.VITE_CLERK_AFTER_SIGN_IN_URL ?? '/dashboard';
const afterSignUpUrl = import.meta.env.VITE_CLERK_AFTER_SIGN_UP_URL ?? '/onboarding';

// Load Clerk script from jsDelivr CDN instead of custom domain (clerk.cyrano.cloud may not resolve).
// This avoids both CSP blocking and ERR_NAME_NOT_RESOLVED when the custom domain isn't set up.
const clerkJSUrl = import.meta.env.VITE_CLERK_JS_URL ?? 'https://cdn.jsdelivr.net/npm/@clerk/clerk-js@4/dist/clerk.browser.js';

if (!clerkPubKey) {
  console.warn('VITE_CLERK_PUBLISHABLE_KEY not found. Clerk authentication will not work.');
}

interface ClerkWrapperProps {
  children: React.ReactNode;
}

const ClerkWrapper: React.FC<ClerkWrapperProps> = ({ children }) => {
  // When key is missing, show fallback so user can return home instead of a blank/error screen
  if (!clerkPubKey) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-warm-background px-4 text-center">
        <img src="/GhostQuillLogo.png" alt="Cyrano" className="w-16 h-16 mb-6 opacity-80" />
        <h1 className="text-xl font-semibold text-space-cadet mb-2">Sign-in not configured</h1>
        <p className="text-cool-gray mb-6 max-w-sm">
          This app needs a Clerk publishable key to sign in. Return home or contact support.
        </p>
        <a
          href="/landing.html"
          className="text-space-cadet font-medium underline hover:no-underline"
        >
          ← Return to home
        </a>
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      clerkJSUrl={clerkJSUrl}
      signInUrl={signInUrl}
      signUpUrl={signUpUrl}
      afterSignInUrl={afterSignInUrl}
      afterSignUpUrl={afterSignUpUrl}
      appearance={{
        variables: {
          colorPrimary: '#19285c', // space-cadet
          colorText: '#060001', // elegant-black
          colorTextSecondary: '#8c83b4', // cool-gray
          borderRadius: '0.5rem'
        },
        elements: {
          card: 'shadow-xl border border-cool-gray/20',
          headerTitle: 'text-space-cadet font-bold',
          headerSubtitle: 'text-cool-gray',
          formButtonPrimary: 'bg-space-cadet hover:bg-space-cadet/90 text-white',
          socialButtonsBlockButton: 'border-cool-gray/30 hover:bg-cool-gray/5',
          formFieldInput: 'border-cool-gray/30 focus:border-space-cadet focus:ring-space-cadet',
          footerActionLink: 'text-space-cadet hover:text-space-cadet/80'
        }
      }}
    >
      {children}
    </ClerkProvider>
  );
};

export default ClerkWrapper;